import { put, takeEvery, all } from "redux-saga/effects";
import { isValidArray } from "../../Services/validators";
import { setErrorStatus } from "../status/action";
import { planQueries } from "../../Utils/queryPlanner";
// import { setErrorStatus } from "../status/action";

// Firestore's 30-disjunction ceiling is fanned out into parallel queries (see
// Utils/queryPlanner), so a wide multi-select no longer has to be rejected outright —
// it just costs more parallel reads. This is the backstop on that cost, not a
// disjunction-count limit: MAX_FAN_OUT_PLANS = 10 plans is ~300 disjunctions' worth of
// filter combinations for one graph refresh.
const MAX_FAN_OUT_PLANS = 10;

// The raw filter object (e.g. { zone: [...], ward: [...], status: [...] }) has one
// array-valued key per Firestore "in" field once it goes through a *QueryBuilder —
// same shape disjunctionCount/planQueries expect from a real triplet array, so build
// a pseudo constraint list directly from it rather than invoking the query builder
// (which needs locations data just to resolve PCTNo labels).
function countFanOutPlans(filter) {
  const pseudoConstraints = Object.values(filter)
    .filter(isValidArray)
    .map((value) => ({ field: "_", operator: "in", value }));

  if (pseudoConstraints.length === 0) return 1;

  return planQueries(pseudoConstraints, { maxDisjunctions: 30 }).length;
}

function* putFilterIfWithinFanOutLimit(action, actionType) {
  const planCount = countFanOutPlans(action.payload.filter);

  if (planCount <= MAX_FAN_OUT_PLANS) {
    yield put({
      type: actionType,
      payload: {
        filter: action.payload.filter
      }
    });
  } else {
    setErrorStatus({
      code: "custom",
      message: "Too many filter values selected — narrow one of the filters and try again"
    });
  }
}

export const tableActionTypes = {
  SET_TABLE_LOADING: "SET_TABLE_LOADING",
  SET_SELECTED_TABLE_DATA: "SET_SELECTED_TABLE_DATA",
  SET_TABLE_FILTER: "SET_TABLE_FILTER",
  SET_TABLE_SORT: "SET_TABLE_SORT",
  SET_TABLE_PAGE_NUMBER: "SET_TABLE_PAGE_NUMBER",
  SET_TABLE_SEARCH_KEY: "SET_TABLE_SEARCH_KEY"
};

function* setSelectedTableDataWorker(action) {
  if (action.payload.table === "LOCATIONS") {
    yield put({
      type: "PUT_SELECTED_LOCATION",
      payload: {
        id: action.payload.data
      }
    });
  }

  if (action.payload.table === "COMPLAINTS") {
    yield put({
      type: "PUT_SELECTED_COMPLAINT",
      payload: {
        data: action.payload.data
      }
    });
  }

  if (action.payload.table === "PROCEDURES") {
    yield put({
      type: "PUT_SELECTED_PROCEDURE",
      payload: {
        id: action.payload.data
      }
    });
  }

  if (action.payload.table === "PROCEDURE_HISTORY") {
    yield put({
      type: "PUT_SELECTED_PROCEDURE_HISTORY",
      payload: {
        id: action.payload.data
      }
    });
  }

  if (action.payload.table === "WORKFORCE") {
    yield put({
      type: "PUT_SELECTED_EMPLOYEE",
      payload: {
        id: action.payload.data
      }
    });
  }

  if (action.payload.table === "REPORTS") {
    yield put({
      type: "PUT_SELECTED_REPORT",
      payload: {
        id: action.payload.data
      }
    });
  }
}

// Every table filter is guarded by the same fan-out cap now (see
// putFilterIfWithinFanOutLimit above) — previously only COMPLAINTS and
// PROCEDURES_HISTORY were guarded, so LOCATIONS / WORKFORCE / CAPTURES / PROCEDURES /
// TRACKS / REPORTS / LIVE_FEED could throw at runtime once a multi-select filter's "in"
// clauses exceeded Firestore's 30-disjunction limit.
const putFilterActionTypeByTable = {
  LOCATIONS: "PUT_LOCATIONS_FILTER",
  COMPLAINTS: "PUT_COMPLAINTS_FILTER",
  WORKFORCE: "PUT_WORKFORCE_FILTER",
  CAPTURES: "PUT_WORKFORCE_CAPTURE_FILTER",
  PROCEDURES: "PUT_PROCEDURES_FILTER",
  TRACKS: "PUT_TRACKS_FILTER",
  REPORTS: "PUT_REPORTS_FILTER",
  PROCEDURES_HISTORY: "PUT_PROCEDURES_HISTORY_FILTER",
  LIVE_FEED: "PUT_LIVE_FEED_FILTER"
};

function* setTableFilterWorker(action) {
  const actionType = putFilterActionTypeByTable[action.payload.table];
  if (!actionType) return;

  yield* putFilterIfWithinFanOutLimit(action, actionType);
}

function* setTableSortWorker(action) {
  if (action.payload.table === "LOCATIONS") {
    yield put({
      type: "PUT_LOCATIONS_SORT",
      payload: {
        sort: action.payload.sort
      }
    });
  }

  if (action.payload.table === "COMPLAINTS") {
    yield put({
      type: "PUT_COMPLAINTS_SORT",
      payload: {
        sort: action.payload.sort
      }
    });
  }

  if (action.payload.table === "WORKFORCE") {
    yield put({
      type: "PUT_WORKFORCE_SORT",
      payload: {
        sort: action.payload.sort
      }
    });
  }

  if (action.payload.table === "REPORTS") {
    yield put({
      type: "PUT_REPORTS_SORT",
      payload: {
        sort: action.payload.sort
      }
    });
  }

  if (action.payload.table === "PROCEDURE_HISTORY") {
    yield put({
      type: "PUT_PROCEDURE_HISTORY_SORT",
      payload: {
        sort: action.payload.sort
      }
    });
  }

  if (action.payload.table === "PROCEDURES") {
    yield put({
      type: "PUT_PROCEDURES_SORT",
      payload: {
        sort: action.payload.sort
      }
    });
  }
}

function* setTablePageNumberWorker(action) {
  if (action.payload.table === "LOCATIONS") {
    yield put({
      type: "PUT_LOCATIONS_PAGE_NUMBER",
      payload: {
        pageNumber: action.payload.pageNumber
      }
    });
  }

  if (action.payload.table === "COMPLAINTS") {
    yield put({
      type: "PUT_COMPLAINTS_PAGE_NUMBER",
      payload: {
        pageNumber: action.payload.pageNumber
      }
    });
  }
  if (action.payload.table === "WORKFORCE") {
    yield put({
      type: "PUT_WORKFORCE_PAGE_NUMBER",
      payload: {
        pageNumber: action.payload.pageNumber
      }
    });
  }

  if (action.payload.table === "PROCEDURE_HISTORY") {
    yield put({
      type: "PUT_PROCEDURE_HISTORY_PAGE_NUMBER",
      payload: {
        pageNumber: action.payload.pageNumber
      }
    });
  }

  if (action.payload.table === "PROCEDURES") {
    yield put({
      type: "PUT_PROCEDURES_PAGE_NUMBER",
      payload: {
        pageNumber: action.payload.pageNumber
      }
    });
  }

  if (action.payload.table === "REPORTS") {
    yield put({
      type: "PUT_REPORTS_PAGE_NUMBER",
      payload: {
        pageNumber: action.payload.pageNumber
      }
    });
  }
}

function* setTableSearchKeyWorker(action) {
  if (action.payload.tableName === "LOCATIONS") {
    yield put({
      type: "PUT_LOCATIONS_SEARCH_KEY",
      payload: { searchKey: action.payload.searchKey }
    });
  }

  if (action.payload.tableName === "COMPLAINTS") {
    yield put({
      type: "PUT_COMPLAINTS_SEARCH_KEY",
      payload: { searchKey: action.payload.searchKey }
    });
  }

  if (action.payload.tableName === "WORKFORCE") {
    yield put({
      type: "PUT_WORKFORCE_SEARCH_KEY",
      payload: { searchKey: action.payload.searchKey }
    });
  }

  if (action.payload.tableName === "PROCEDURES") {
    yield put({
      type: "PUT_PROCEDURES_SEARCH_KEY",
      payload: { searchKey: action.payload.searchKey }
    });
  }

  if (action.payload.tableName === "PROCEDURE_HISTORY") {
    yield put({
      type: "PUT_PROCEDURE_HISTORY_SEARCH_KEY",
      payload: { searchKey: action.payload.searchKey }
    });
  }

  if (action.payload.tableName === "REPORTS") {
    yield put({
      type: "PUT_REPORTS_SEARCH_KEY",
      payload: { searchKey: action.payload.searchKey }
    });
  }
}

export default function* tableWatcher() {
  yield all([
    takeEvery("SET_SELECTED_TABLE_DATA", setSelectedTableDataWorker),
    takeEvery("SET_TABLE_FILTER", setTableFilterWorker),
    takeEvery("SET_TABLE_SORT", setTableSortWorker),
    takeEvery("SET_TABLE_PAGE_NUMBER", setTablePageNumberWorker),
    takeEvery("SET_TABLE_SEARCH_KEY", setTableSearchKeyWorker)
  ]);
}

// function* setTableLoading(value) {
//   yield put({
//     type: "SET_TABLE_LOADING",
//     payload: {
//       loading: value,
//     },
//   });
// }
